/**
 * @graphql agg
 */
import { graphql } from 'react-relay';

import { useMutationInterop } from '@confluence/apollo-relay-interop';

import type {
	useCreateObjectCommentMutation$data,
	useCreateObjectCommentMutation$variables,
} from './__generated__/useCreateObjectCommentMutation.graphql';
import { type Comment } from './useAllCommentsData';

// This hook returns a mutation function to create a new comment on an object. Because the new
// Comments Data Manager caches all the data for comments in Relay, we need to define the mutation
// here so that the cache can be updated fully when a new comment is created - and that means the
// mutation needs to return the full comment data structure that we are using in the returned query
// part of the mutation.  Since that returned query is quite large, we define it here in its own
// hook to avoid having it in many places in the codebase - which makes the code harder to read and
// could lead to the multiple copies getting out of sync.

// Data Mutation Definition:

// Unfortunately our Relay-friendly type generation tools don't support using GQL fragments yet -
// but in order to keep the definition of the comment fields in sync (so the cache update fragment
// is always in sync with the full query) we will need to manually make any changes to one
// definition in any others as well, until our tooling supports fragments and then we move to a
// more fragmented-based approach.  Note there are also comment queries or fragments in the
// following files that need to be kept in sync as well:
// - next/packages/comments-data-manager/src/useAllCommentsData.ts
// - next/packages/comments-data/src/hooks/useCommentById.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts

const CreateObjectCommentMutation = graphql`
	mutation useCreateObjectCommentMutation(
		$pageId: ID!
		$cloudId: ID!
		$input: CreateCommentInput!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				__typename
				displayName
				permissionType
				... on KnownUser {
					accountId
					permissionType
				}
				profilePicture {
					path
				}
				type
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			container {
				id
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				editui
				webui
			}
			location {
				__typename
				type
				... on FooterComment {
					commentResolveProperties {
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
				... on InlineComment {
					inlineMarkerRef
					inlineResolveProperties {
						isDangling
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
					inlineText
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					count
					emojiId
					reacted
				}
			}
			# don't need the 'replies' field because new comments are always created without any replies
			version {
				by {
					__typename
					type
					displayName
					... on KnownUser {
						accountId
					}
				}
				friendlyWhen
				number
				when
			}
		}
	}
`;

export const useCreateObjectCommentMutation = () => {
	const [createObjectCommentFn] = useMutationInterop<
		useCreateObjectCommentMutation$data,
		useCreateObjectCommentMutation$variables
	>(CreateObjectCommentMutation);

	return createObjectCommentFn;
};

export const addEmptyRepliesArray = (
	commentData: useCreateObjectCommentMutation$data['createFooterComment'],
): Comment => {
	return commentData
		? {
				...commentData,
				replies: [],
			}
		: undefined;
};
